/**
 * Data contract for the Executive Report page.
 *
 * Это зеркало `ProjectReportData` из GraphQL: те же имена, та же
 * вложенность, те же коды перечислений. Страница презентационная — она
 * получает снимок отчёта и печатает его, поэтому переименовывать поля по
 * дороге незачем: лишний словарь между API и вёрсткой живёт ровно до
 * первого расхождения, а потом врёт.
 *
 * Что остаётся за страницей:
 *
 *   - подписи кодов (`content.ts`): «Требует срочного вмешательства» для
 *     `URGENT`, «Регулярность работы» для `ACTIVITY`;
 *   - форматирование (`helpers.ts`): разряды, знак рубля, склонения,
 *     «7 июля 2026», «18–30 апреля»;
 *   - служебные символы (`typograph.ts`): текст приходит сырым.
 *
 * Деньги — целые рубли числом, даты — ISO `ГГГГ-ММ-ДД`.
 *
 * @format
 */

import type {SpacingTokenName} from 'ui/components/layout/spacing';

/** Календарная дата, ISO `ГГГГ-ММ-ДД`. Время и зона не подразумеваются. */
export type IsoDate = string;

/** Момент времени, ISO 8601. */
export type IsoDateTime = string;

/* ─────────────────────────── перечисления ─────────────────────────── */

/** Тон вывода: задаёт цвет линейки и выделенной величины. */
export type Severity = 'GOOD' | 'ALERT' | 'BAD';

/** Категория светофора кампаний. `GRAY` — кампания без расхода за период. */
export type CampaignColor = 'RED' | 'YELLOW' | 'GREEN' | 'GRAY';

/** Полоса шкалы балла. */
export type ScoreZoneKey = 'URGENT' | 'NEEDS_ATTENTION' | 'NORMAL' | 'EXCELLENT';

/** Слагаемое балла. */
export type ScorePartKey =
  | 'KPI_PLAN_FACT'
  | 'RISK_SHARE'
  | 'ZERO_RESULT'
  | 'CRITICAL_INCIDENTS'
  | 'REGULAR_INCIDENTS'
  | 'ACTIVITY';

/**
 * Показатель ожидания точки контроля.
 *
 * `FEED_ERRORS` и `TEAM_RESPONSE` объявлены в схеме, но бэкенд их не
 * порождает — страница обязана пережить их отсутствие и не строить на них
 * вёрстку.
 */
export type ExpectationMetric =
  | 'CRITICAL_INCIDENTS'
  | 'KPI'
  | 'FEED_ERRORS'
  | 'MANUAL_EDIT_DAYS'
  | 'TEAM_RESPONSE';

/** Единица измеримой величины. */
export type Unit = 'MONEY' | 'ENQUIRY' | 'DAY' | 'POINT' | 'PERCENT' | 'COUNT';

/** Как читать величину цели. По умолчанию `EXACT`. */
export type Bound = 'EXACT' | 'AT_MOST' | 'AT_LEAST';

/** Откуда взялся план по обращениям. */
export type PlanSource = 'MEDIAPLAN' | 'DERIVED';

/** Чем был занят день по времени событий. */
export type EditKind = 'MANUAL' | 'AUTO' | 'IDLE';

/**
 * Тип дня по производственному календарю.
 *
 * Значения повторяют enum источника. Выводить их из дня недели нельзя:
 * переносы рабочих суббот и праздники арифметике не подчиняются.
 */
export type DayType = 'WORKDAY' | 'WEEKEND' | 'HOLIDAY' | 'SHORTENED';

/** Применялась ли секция отчёта. */
export type SectionStatus = 'APPLIED' | 'NOT_APPLICABLE';

/**
 * Почему секция правок не строилась.
 *
 * `PERIOD_BEFORE_TRACKING` — проект заведён в AdSensor позже начала
 * периода, история правок за него неполна. `HISTORY_UNAVAILABLE` —
 * журнал правок недоступен; в готовом отчёте почти не встречается:
 * недоступная история роняет сборку на ретрай, а не доводит её до
 * `READY`.
 */
export type EditsStatusReason = 'PERIOD_BEFORE_TRACKING' | 'HISTORY_UNAVAILABLE';

/** Срочность задачи: P1 — 3 дня, P2 — 7, P3 — 14. */
export type TaskPriority = 'P1' | 'P2' | 'P3';

/** Оценка эталонного ответа команды. */
export type AnswerQuality = 'GOOD' | 'BAD';

/** Основной KPI медиаплана. Отчёт строится только по этим двум метрикам. */
export type KpiName = 'CPL' | 'DRR';

/** Сравнение факта с планом. */
export type KpiStatus = 'IN_PLAN' | 'DEVIATION' | 'NOT_APPLICABLE';

/** Критичность сенсора на момент генерации отчёта. */
export type SensorCategory = 'CRITICAL' | 'HYGIENE';

/** Рекламная система. */
export type AdSystem =
  | 'YANDEX'
  | 'GOOGLE'
  | 'FACEBOOK'
  | 'VKONTAKTE'
  | 'VKONTAKTE_ADS'
  | 'METRIKA'
  | 'WEBDAV';

/** Стадия сборки отчёта. */
export type ReportStatus = 'PENDING' | 'COLLECTING' | 'GENERATING' | 'READY' | 'ERROR';

/**
 * Почему сборка прервана.
 *
 * `MEDIAPLAN_NOT_FOUND`, `KPI_NOT_SUPPORTED` и `CHECKS_NEVER_COLLECTED`
 * окончательны: повторная сборка тех же данных снова упрётся в то же самое —
 * сперва меняют сам проект (заводят медиаплан, правят его KPI).
 * `MEDIAPLAN_NOT_FOUND` — не «нет плана на период»: сборка сама берёт план,
 * действующий на момент сборки, если плана на сам период нет. Причина
 * срабатывает, только когда медиаплана нет вовсе — ни на период, ни на
 * момент сборки.
 */
export type ReportErrorReason =
  | 'INTERNAL'
  | 'STATS_UNAVAILABLE'
  | 'MEDIAPLAN_NOT_FOUND'
  | 'KPI_NOT_SUPPORTED'
  | 'CHECKS_NEVER_COLLECTED';

/* ──────────────────────────── навигация ──────────────────────────── */

/** Anchor ids — used by the sticky nav and by "Подробнее →" jumps. */
export const SECTION_IDS = {
  summary: 'summary',
  score: 'score',
  risks: 'risks',
  zeroResult: 'zeroResult',
  campaigns: 'campaigns',
  clients: 'clients',
  team: 'team',
  incidents: 'incidents',
  questions: 'questions',
  tasks: 'tasks',
  control: 'control',
  notifications: 'notifications',
} as const;

export type SectionId = (typeof SECTION_IDS)[keyof typeof SECTION_IDS];

/** Этаж, куда ведёт «Подробнее» из вывода первого экрана. */
export type Section =
  | 'SCORE'
  | 'RISKS'
  | 'CAMPAIGNS'
  | 'CLIENTS'
  | 'TEAM'
  | 'INCIDENTS'
  | 'QUESTIONS'
  | 'TASKS'
  | 'CONTROL';

/**
 * Journey stage a nav anchor belongs to. The four stages come from the review
 * call: what is happening → what to do about it → when we check → how not to
 * miss that check.
 */
export type JourneyStage = 'diagnosis' | 'action' | 'control' | 'cta';

export interface NavAnchor {
  id: SectionId;
  label: string;
  stage: JourneyStage;
}

/* ───────────────────────── общие величины ────────────────────────── */

/**
 * Измеримая величина: значение, единица и знаменатель доли.
 *
 * `of` — знаменатель для показателей вида «26 дней из 128»: два числа
 * одной природы, которые читаются только вместе.
 */
export interface Amount {
  value: number;
  unit: Unit;
  of?: number | null;
}

/**
 * Текущее или целевое состояние ожидания.
 *
 * Заполнено ровно одно из двух: `amount` — когда цель измерима, `text` —
 * когда качественная («получен», «2–3 дня в неделю»).
 */
export interface Target {
  amount?: Amount | null;
  bound?: Bound | null;
  text?: string | null;
}

/** Кусок заголовка; сегмент с `emphasis` красится в цвет тяжести. */
export interface TextSegment {
  text: string;
  emphasis: boolean;
}

/* ────────────────────────── реквизиты ────────────────────────────── */

export interface ReportMeta {
  /** Рекламная система, по которой собран отчёт. */
  adSystem: AdSystem;
  /** Тип медиаплана, по нему отфильтрованы кампании. Странице не нужен. */
  mediaplanType?: string | null;
  periodStart: IsoDate;
  periodEnd: IsoDate;
}

/* ─────────────────────────── этажи отчёта ────────────────────────── */

/** Один вывод первого экрана. */
export interface SummaryPoint {
  severity: Severity;
  /** Заголовок сегментами: величина выделена отдельным куском. */
  title: TextSegment[];
  /** Одно предложение «и что с того». */
  detail: string;
  anchor: Section;
}

/** Одна полоса шкалы балла; границы включительные. */
export interface ScoreZone {
  key: ScoreZoneKey;
  from: number;
  to: number;
}

/** Одно слагаемое балла со своим потолком. */
/**
 * Публичная ссылка на отчёт.
 *
 * Одна на отчёт: сколько отчётов, столько и ссылок, у каждой свой пароль.
 * Срока жизни нет — ссылка работает, пока жив отчёт и пока её не удалили.
 */
export interface PublicLink {
  /** Полный адрес, который отправляют получателю. */
  url: string;
  /**
   * Пароль к этой ссылке. Генерирует система, человек его не придумывает.
   *
   * `null` — пароль перестал читаться после ротации ключа шифрования на
   * бэкенде. Ссылка при этом рабочая: тот, кому пароль уже сообщили,
   * по-прежнему открывает отчёт.
   */
  password: string | null;
}

export interface ScorePart {
  key: ScorePartKey;
  got: number;
  max: number;
  /** Причина вычета обычными словами; заполняет генератор. */
  note?: string | null;
}

/** Один сработавший сенсор внутри кампании под риском. */
export interface RiskSensor {
  key: string;
  name: string;
  /** Сколько находок этого сенсора в кампании за день. */
  found: number;
}

/**
 * Кампания под риском — одна из пяти самых дорогих.
 *
 * `spend` суточный: кампания попала сюда потому, что в последний день
 * периода в ней сработали критичные сенсоры, и её дневной расход и есть
 * вклад в общую сумму риска.
 */
export interface RiskRow {
  campaignId: string;
  name?: string | null;
  spend?: number | null;
  /** Срабатываний критичных сенсоров за день. */
  criticalFound: number;
  sensors?: RiskSensor[] | null;
  /** Объяснение простым языком для руководителя. */
  simple?: string | null;
  /** Комментарий для специалиста. */
  professional?: string | null;
}

/** Кампания из хвоста: только имя и расход. */
export interface RiskRestRow {
  campaignId: string;
  name?: string | null;
  spend?: number | null;
}

/**
 * Кампания блока расхода вхолостую.
 *
 * `kpiFact` пуст, когда факт не из чего посчитать, — в блоке
 * `zeroResult` это норма, кампания туда и попала из-за отсутствия
 * результата.
 */
export interface WasteCampaign {
  campaignId: string;
  name?: string | null;
  /** Расход за период. */
  spend?: number | null;
  /** Клики за период. */
  clicks?: number | null;
  kpiFact?: number | null;
}

/**
 * Блок расхода вхолостую: сумма, доля и кампании от большего расхода к
 * меньшему. Текстов модели в блоке нет — только числа.
 */
export interface Waste {
  /** Расход кампаний блока за период. */
  spend?: number | null;
  /** Доля от `project.spend` в процентах. */
  pct?: number | null;
  campaigns?: WasteCampaign[] | null;
}

export interface Campaign {
  campaignId: string;
  name?: string | null;
  /** Расход за период, рубли. */
  spend?: number | null;
  color: CampaignColor;
  /** Почему кампания в этом цвете. Пусто у зелёных и серых. */
  reason?: string | null;
  /** Факт основного KPI по этой кампании. */
  kpiFact?: number | null;
  /** Плановое значение основного KPI. */
  kpiPlan?: number | null;
  /** Отклонение факта от плана в процентах со знаком. */
  deviationPct?: number | null;
}

/** Основной KPI медиаплана и его исполнение за период. */
export interface Kpi {
  name: KpiName;
  plan?: number | null;
  fact?: number | null;
  deviationPct?: number | null;
  status: KpiStatus;
}

/**
 * Этаж клиентов. Пусто, когда основной KPI не `CPL`: плановой цены
 * обращения тогда не существует, и план по обращениям взять неоткуда.
 */
export interface Clients {
  budget: number;
  cplPlan?: number | null;
  cplFact?: number | null;
  leadsPlan?: number | null;
  leadsFact: number;
  /** Недобор обращений; ноль, когда плана нет или он выполнен. */
  gap?: number | null;
  planSource: PlanSource;
}

/** Один день периода в календаре правок. */
export interface EditDay {
  date: IsoDate;
  count: number;
  kind: EditKind;
  /**
   * Тип дня из производственного календаря. Пусто — календарь не пришёл;
   * тогда все дни рисуются рабочими, как и до его появления.
   */
  dayType?: DayType | null;
}

/** Отрезок периода без единой правки. */
export interface Pause {
  from: IsoDate;
  to: IsoDate;
  days: number;
  /** Пауза пересекается с активным критичным инцидентом — худший случай. */
  overIncident: boolean;
}

/** Правки в рекламном кабинете за период. */
export interface Edits {
  /** Сколько правок всего. */
  total?: number | null;
  /** Из них точечных по типу правки — не про время суток. */
  point?: number | null;
  /** Из них массовых по типу правки. Не путать с `EditDay.kind`. */
  mass?: number | null;
  /** В скольких днях периода была хотя бы одна правка. */
  daysWithEdits?: number | null;
  /** В скольких днях были дневные правки — сумма дней с `kind: MANUAL`. */
  manualDays?: number | null;
  /** Все дни периода подряд, включая пустые. */
  calendar?: EditDay[] | null;
  /** Отрезки без правок длиннее порога. */
  pauses?: Pause[] | null;
  /** Вывод по календарю правок; заполняет генератор. */
  conclusion?: string | null;
}

/**
 * Строилась ли секция правок и почему нет.
 *
 * `NOT_APPLICABLE` означает, что `edits` придёт пустым, а слагаемое балла
 * `ACTIVITY` — полным: регулярность работы никто не измерял, вычитать не
 * за что.
 */
export interface EditsStatus {
  status: SectionStatus;
  /** Причина отказа; пусто при `APPLIED`. */
  reason?: EditsStatusReason | null;
}

/** Одна точка накопительного ряда инцидентов. */
export interface IncidentPoint {
  date: IsoDate;
  /** Накоплено находок на конец дня, вместе с базой. */
  found: number;
  /** Накоплено закрытых на конец дня, вместе с базой. */
  fixed: number;
}

/** Накопительный ряд находок и закрытий с базой на начало периода. */
export interface Incidents {
  baselineFound: number;
  baselineFixed: number;
  totalFound: number;
  totalFixed: number;
  cumulative: IncidentPoint[];
}

/** Находки одного сенсора. */
export interface Sensor {
  key: string;
  name?: string | null;
  category: SensorCategory;
  campaigns?: number | null;
  newCampaigns?: number | null;
  lastSeenOn?: IsoDate | null;
  lastRows?: number | null;
  lastCampaigns?: number | null;
}

/** Один из двух эталонных ответов команды. */
export interface AnswerExample {
  quality: AnswerQuality;
  /** Как это звучит из уст команды. */
  text: string;
  /** Почему ответ засчитан или нет. */
  why: string;
}

export interface Question {
  question: string;
  /** На чём основан вопрос: конкретная цифра или факт из отчёта. */
  basis?: string | null;
  answers?: AnswerExample[] | null;
}

/**
 * Кампания, которой касается задача.
 *
 * `name` бывает пустым: идентификатор из ответа модели мог не найтись
 * среди кампаний периода. Бейдж без имени рисовать нечем.
 */
export interface TaskCampaign {
  campaignId: string;
  name?: string | null;
}

export interface Task {
  priority: TaskPriority;
  title: string;
  description: string;
  campaigns?: TaskCampaign[] | null;
}

/** Одно измеримое ожидание к дате следующей проверки. */
export interface Expectation {
  metric: ExpectationMetric;
  from: Target;
  to: Target;
  /** Почему цель именно такая; заполняет генератор. */
  note?: string | null;
}

/** Точка контроля: когда отчёт посмотрят заново и что должно измениться. */
export interface Control {
  /** Конец периода плюс календарный месяц. */
  nextReportDate: IsoDate;
  expectations: Expectation[];
}

/* ──────────────────────────── the whole ──────────────────────────── */

/**
 * Числовая часть отчёта и текстовые выводы.
 *
 * При `narrativeFailed` пустеют только текстовые поля, а не списки и
 * структуры, в которых они лежат:
 *
 *   - `summary` становится `null` целиком (не пустым списком — так
 *     оговорено в схеме);
 *   - `riskNote` и `sensorWarning` становятся `null`;
 *   - `questions` и `tasks` становятся пустыми списками;
 *   - внутри `risks[]` пустеют только `simple`/`professional` — сам
 *     список кампаний со всеми структурными полями (`campaignId`,
 *     `spend`, `criticalFound`, `sensors`) приходит полным;
 *   - то же для `note`/`conclusion`/`reason` внутри структур
 *     (`ScorePart`, `Edits`, `Campaign`, `Expectation`) — пустеет само
 *     поле, а не контейнер.
 *
 * `risks`, `riskRest`, `zeroResult`, `campaigns`, `incidents` и прочая
 * числовая часть от генерации текста не зависят и приходят полными
 * независимо от `narrativeFailed`.
 *
 * `edits` — исключение, и не из-за текста: секция правок может не
 * строиться вовсе. Тогда в готовом отчёте `edits` пуст, а `editsStatus`
 * называет причину. Читать `edits` без проверки на пустоту нельзя.
 *
 * Пустыми перечисленные текстовые поля бывают и при удачной генерации —
 * сказать оказалось нечего; разница в том, что при провале генерации
 * пусто везде сразу.
 */
export interface ReportProject {
  /** Название проекта. */
  name?: string | null;
  /** Вернула ли выгрузка хотя бы одну кампанию нужного типа. */
  activeInPeriod?: boolean | null;
  /** Выводы не сгенерированы; числовая часть при этом полная. */
  narrativeFailed?: boolean | null;

  /** Оценка проекта, 0–100. */
  score?: number | null;
  scoreZone?: ScoreZoneKey | null;
  /** Все полосы шкалы — границы правятся конфигом без релиза. */
  scoreZones?: ScoreZone[] | null;
  /** Слагаемые оценки, сумма `got` которых равна `score`. */
  scoreParts?: ScorePart[] | null;

  summary?: SummaryPoint[] | null;

  /** Расход всех кампаний за период — знаменатель для `zeroResult.pct`. */
  spend?: number | null;
  /** Конверсии всех кампаний за период. */
  leads?: number | null;
  /** Расход на конверсию за период. */
  cpl?: number | null;
  kpi?: Kpi | null;
  clients?: Clients | null;

  /** Суточный расход кампаний, где в последний день периода сработали критичные сенсоры. */
  risk?: number | null;
  /** Суточный расход всех кампаний за тот же день — знаменатель `riskPct`. */
  riskDailySpend?: number | null;
  /** Доля `risk` от `riskDailySpend` в процентах. */
  riskPct?: number | null;
  /** До пяти самых дорогих кампаний под риском. */
  risks?: RiskRow[] | null;
  /** Остальные кампании под риском: вместе с `risks` дают полную сумму. */
  riskRest?: RiskRestRow[] | null;
  /** Объяснение нулевого риска; заполнено только когда `risk = 0`. */
  riskNote?: string | null;
  /** Главная тревога сенсоров, не выраженная в деньгах. */
  sensorWarning?: string | null;
  /** Кампании с расходом за период, не давшие результата по основному KPI. */
  zeroResult?: Waste | null;

  campaigns?: Campaign[] | null;
  edits?: Edits | null;
  /**
   * Строилась ли секция правок. Пусто — отчёт собран до появления поля;
   * тогда `edits` заполнен, как и раньше. Признак заглушки — только
   * `editsStatus?.status === 'NOT_APPLICABLE'`, а не пустой `edits`.
   */
  editsStatus?: EditsStatus | null;
  incidents?: Incidents | null;
  sensors?: Sensor[] | null;
  sensorsResolved?: Sensor[] | null;

  questions?: Question[] | null;
  tasks?: Task[] | null;
  control?: Control | null;
}

/** Снимок отчёта, зафиксированный на момент `generatedAt`. */
export interface ReportData {
  meta: ReportMeta;
  project: ReportProject;
}

/**
 * Состояние подписок на момент чтения.
 *
 * Не часть снимка: подписку переключают когда угодно, и в документе
 * месячной давности тумблер обязан показывать сегодняшнее положение дел.
 * Приходит из `company.notifications`, а не из отчёта.
 */
export interface NotificationsData {
  email: boolean;
  telegram: boolean;
  telegramUrl: string;
}

/** Shared gap used between the top-level page sections. */
export const PAGE_GAP: SpacingTokenName = 'xl';
